import type { FC } from 'react';
import { CloseButton } from '@mantine/core';
import { MdOutlineTouchApp } from 'react-icons/md';
import type { TooltipRenderProps } from 'react-joyride';
import Button from '@srm/ui/button';
import { useTourControls } from './TourControlsContext';
import type { ITourStepData } from '@srm/core/contracts/tours';
import './TourTooltip.scss';

const TourTooltip: FC<TooltipRenderProps> = ({
    backProps,
    primaryProps,
    skipProps,
    index,
    size,
    step,
    tooltipProps,
    isLastStep,
}) => {
    const { goToStep, canGoBack, actionDone } = useTourControls();
    const stepData = step.data as ITourStepData | null;
    const hasBranches = !!stepData?.branches?.length;
    const awaitingAction = !!stepData?.action && !actionDone;

    return (
        <div {...tooltipProps} aria-modal={!stepData?.action && !stepData?.hideOverlay} className="tour-tooltip">
            <header className="tour-tooltip__header">
                <span className="tour-tooltip__counter">
                    Шаг {index + 1} из {size}
                </span>
                <CloseButton onClick={skipProps.onClick} aria-label="Завершить тур" size="sm" />
            </header>
            {size > 0 && (
                <div
                    className="tour-tooltip__progress"
                    role="progressbar"
                    aria-label="Прогресс тура"
                    aria-valuemin={0}
                    aria-valuemax={size}
                    aria-valuenow={index + 1}
                >
                    {Array.from({ length: size }, (_, i) => (
                        <span key={i} className="tour-tooltip__segment" data-filled={i <= index} />
                    ))}
                </div>
            )}
            <div className="tour-tooltip__body" aria-live="polite">
                {step.title && <h2 className="tour-tooltip__title">{step.title}</h2>}
                <div className="tour-tooltip__content">{step.content}</div>
                {hasBranches && (
                    <div className="tour-tooltip__branches">
                        {stepData?.branches?.map((branch, i) => (
                            <Button
                                key={i}
                                variant="secondary"
                                size="sm"
                                onClick={() => goToStep(branch.goTo, branch.label)}
                            >
                                {branch.label}
                            </Button>
                        ))}
                    </div>
                )}
                {awaitingAction && (
                    <div className="tour-tooltip__waiting" role="status">
                        <MdOutlineTouchApp size={18} aria-hidden />
                        <span>{stepData?.action?.waitingLabel || 'Выполните действие...'}</span>
                    </div>
                )}
            </div>
            <footer className="tour-tooltip__footer">
                <Button variant="text" size="sm" {...skipProps}>
                    Завершить тур
                </Button>
                <div className="tour-tooltip__navigation">
                    {canGoBack && (
                        <Button variant="secondary" size="sm" {...backProps}>
                            Назад
                        </Button>
                    )}
                    {!awaitingAction && !hasBranches && (
                        <Button variant="primary" size="sm" {...primaryProps}>
                            {isLastStep ? 'Завершить' : 'Далее'}
                        </Button>
                    )}
                </div>
            </footer>
        </div>
    );
};

export default TourTooltip;
